import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import IssueCard from '@/components/IssueCard';
import IssueMap from '@/components/IssueMap';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, Plus, MapPin, Calendar, User } from 'lucide-react';
import { Issue } from '@/types';
import { storageService } from '@/lib/storage';
import { authService } from '@/lib/auth';
import { format } from 'date-fns';
import IssueFeedbackModal from '@/components/IssueFeedbackModal';



export default function Dashboard() {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [filteredIssues, setFilteredIssues] = useState<Issue[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    const loadedIssues = storageService.getIssues();
    
    // Filter issues based on user role
    const userIssues = currentUser.role === 'admin' 
      ? loadedIssues 
      : loadedIssues.filter(issue => issue.reportedBy === currentUser.name);
    
    setIssues(userIssues);
    setFilteredIssues(userIssues);
  }, [currentUser, navigate]);

  useEffect(() => {
    let filtered = issues;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(issue =>
        issue.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.location.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(issue => issue.status === statusFilter);
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(issue => issue.category === categoryFilter);
    }

    setFilteredIssues(filtered);
  }, [issues, searchTerm, statusFilter, categoryFilter]);

  const handleIssueDetails = (issue: Issue) => {
    setSelectedIssue(issue);
    setShowIssueDialog(true);
  };

  const getStatusStats = () => {
    const stats = {
      total: issues.length,
      pending: issues.filter(i => i.status === 'pending').length,
      inProgress: issues.filter(i => i.status === 'in_progress').length,
      resolved: issues.filter(i => i.status === 'resolved').length,
    };
    return stats;
  };

  const stats = getStatusStats();

  if (!currentUser) {
    return null;
  }

  return (
    <Layout title={currentUser.role === 'admin' ? 'Admin Dashboard' : 'My Reports'}>
      <div className="space-y-6 p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Issues</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <MapPin className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <Calendar className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <User className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Resolved</p>
                <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <MapPin className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Actions and Filters */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 flex-1">
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search issues..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="pothole">Pothole</SelectItem>
                <SelectItem value="street_light">Street Light</SelectItem>
                <SelectItem value="garbage">Garbage</SelectItem>
                <SelectItem value="traffic_sign">Traffic Sign</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button onClick={() => navigate('/report')}>
            <Plus className="h-4 w-4 mr-2" />
            Report Issue
          </Button>
        </div>

        {/* Content Tabs */}
        <Tabs defaultValue="list" className="space-y-6">
          <TabsList>
            <TabsTrigger value="list">List View</TabsTrigger>
            <TabsTrigger value="map">Map View</TabsTrigger>
          </TabsList>
          
          <TabsContent value="list" className="space-y-6">
            {filteredIssues.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No issues found</h3>
                <p className="text-gray-600 mb-4">
                  {issues.length === 0 
                    ? "You haven't reported any issues yet." 
                    : "No issues match your current filters."}
                </p>
                <Button onClick={() => navigate('/report')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Report Your First Issue
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredIssues.map((issue) => (
                  <IssueCard
                    key={issue.id}
                    issue={issue}
                    onViewDetails={handleIssueDetails}
                    showActions={currentUser.role === 'admin'}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="map">
            <IssueMap
              issues={filteredIssues}
              selectedIssue={selectedIssue}
              onIssueSelect={(issue) => {
                setSelectedIssue(issue);
                handleIssueDetails(issue);
              }}
            />
          </TabsContent>
        </Tabs>

        {/* Issue Details Dialog */}
        <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedIssue && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between">
                    <span>{selectedIssue.title}</span>
                    <Badge className={
                      selectedIssue.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      selectedIssue.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }>
                      {selectedIssue.status.replace('_', ' ')}
                    </Badge>
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  {selectedIssue.image && (
                    <img
                      src={selectedIssue.image}
                      alt={selectedIssue.title}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  )}
                  {selectedIssue.status === 'resolved' && !selectedIssue.rating && (
  <Button
    className="w-full mt-4"
    onClick={() => setShowFeedback(true)}
  >
    Give Feedback
  </Button>
)}
{selectedIssue.rating && (
  <div className="mt-4 rounded-lg border bg-green-50 p-3 text-sm">
    <p>⭐ Rating: {selectedIssue.rating}/5</p>
    {selectedIssue.feedback && (
      <p className="mt-1 text-gray-600">💬 {selectedIssue.feedback}</p>
    )}
  </div>
)}


                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Category:</span>
                      <p className="capitalize">{selectedIssue.category.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Priority:</span>
                      <p className="capitalize">{selectedIssue.priority}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Reported By:</span>
                      <p>{selectedIssue.reportedBy}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Reported On:</span>
                      <p>{format(selectedIssue.reportedAt, 'MMM dd, yyyy')}</p>
                    </div>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-600">Description:</span>
                    <p className="mt-1">{selectedIssue.description}</p>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-600">Location:</span>
                    <p className="mt-1 flex items-center space-x-1">
                      <MapPin className="h-4 w-4" />
                      <span>{selectedIssue.location.address || `${selectedIssue.location.lat}, ${selectedIssue.location.lng}`}</span>
                    </p>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
        {selectedIssue && (
  <IssueFeedbackModal
    issue={selectedIssue}
    open={showFeedback}
    onClose={() => {
      setShowFeedback(false);
      const updated = storageService.getIssues();
      setIssues(updated);
      setFilteredIssues(updated);
    }}
  />
)}

      </div>
    </Layout>
  );
}